import { cloneVNode, useSlots, ref, watch, onUnmounted, onMounted, inject, provide } from "vue";

const SymbolTooltipProvider = Symbol("TooltipProvider");

export function useTooltipProvider() {
    const tooltips = ref(new Map());

    provide(SymbolTooltipProvider, tooltips);

    return {
        showTooltip(anchorElement, refText) {
            tooltips.value.set(anchorElement, {
                anchorElement,
                refText,
                visible: false,
            });
        },

        hideTooltip(anchorElement) {
            tooltips.value.delete(anchorElement);
        },

        get tooltips() {
            return tooltips;
        },
    };
}

function useAutoUpdate() {
    const refRunning = ref(false);

    function cancel() {
        refRunning.value = false;
    }

    function start(cb) {
        if (refRunning.value)
            return;

        // Start animation.
        refRunning.value = true;

        // Request animation loop.
        requestAnimationFrame(function next(ms) {
            if (refRunning.value) {
                cb(ms);
                requestAnimationFrame(next);
            }
        });
    }

    // Cancel animation on unmount.
    onUnmounted(cancel);

    return [ start, cancel ];
}

function useEventListenerAutoSwitch(cbWatcher, name, cbFunc, options) {

    // Attach listener on mount.
    onMounted(() => {
        cbWatcher()?.addEventListener(name, cbFunc, options);
    });

    // Cleanup listener on unmount.
    onUnmounted(() => {
        cbWatcher()?.removeEventListener(name, cbFunc);
    });

    // Detect value change.
    watch(cbWatcher, (value, oldValue) => {

        // Remove old listener.
        oldValue?.removeEventListener(name, cbFunc);

        // Attach new listener.
        value?.addEventListener(name, cbFunc, options);
    });
}

export default {

    props: [ "label" ],

    setup(props) {
        const refNode           = ref(null);
        const refTooltipArrow   = ref(null);
        const refTooltipLabel   = ref(null);
        const refHover          = ref(false);
        const slots             = useSlots();
        const [ start, cancel ] = useAutoUpdate();

        function calculatePosition(refRect) {
            const anchorPos     = refRect.width / 2;
            const positionXA    = refRect.x + anchorPos;
            const positionXB    = anchorPos;

            console.log(positionXA, positionXB)

            return {
                left    : Math.max(positionXA, positionXB),
                top     : refRect.y - 6,
            };
        }

        function onMouseEnter() {
            let oldRect = {};

            refHover.value = true;

            start(() => {
                const refDiv        = getRefNode();
                const refRect       = refDiv.getBoundingClientRect();
                const hasChanged    = refRect.x !== oldRect.x
                    || refRect.y !== oldRect.y
                    || refRect.width !== oldRect.width
                    || refRect.height !== oldRect.height;

                if (hasChanged) {
                    const position = calculatePosition(refRect);

                    refTooltipArrow.value.style.left  = `${position.left}px`;
                    refTooltipLabel.value.style.left  = `${position.left}px`;
                    refTooltipArrow.value.style.top   = `${position.top + 4}px`;
                    refTooltipLabel.value.style.top   = `${position.top}px`;
                    oldRect = refRect;
                }
            });
        }

        function onMouseLeave() {
            refHover.value = false;
            cancel();
        }

        function getRefNode() {
            const node = refNode.value;

            if (node)
                return node instanceof Element
                    ? node
                    : node.$el;
        }

        return () => {
            const tooltipElement = cloneVNode(slots.default()[0], {
                onmouseenter    : onMouseEnter,
                onmouseleave    : onMouseLeave,
                ref             : refNode
            });

            function renderTooltip() {
                if (refHover.value) {
                    const refDiv    = getRefNode();
                    const refRect   = refDiv.getBoundingClientRect();
                    const position  = calculatePosition(refRect);

                    return (
                        <>
                            <div style={{
                                background      : "#101010",
                                position        : "fixed",
                                transform       : "translate(-50%, calc(-50% - 12px)) rotate(45deg)",
                                left            : `${position.left}px`,
                                top             : `${position.top + 4}px`,
                                width           : "16px",
                                height          : "16px",
                            }} ref={refTooltipArrow} />

                            <div style={{
                                background      : "#101010",
                                color           : "#FFFFFF",
                                borderRadius    : ".25rem",
                                position        : "fixed",
                                transform       : "translate(-50%, -100%)",
                                left            : `${position.left}px`,
                                top             : `${position.top}px`,
                                padding         : "0 .5rem",
                                lineHeight      : "2rem",
                            }} ref={refTooltipLabel}>
                                {props.label}
                            </div>
                        </>
                    );
                }
            }

            return (
                <>
                    {renderTooltip()}
                    {tooltipElement}
                </>
            );
        };
    },
};
